import { apiClient } from './api-client';

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  candidateId: string;
}

export interface UploadTask {
  id: string;
  file: File;
}

export interface UploadResult {
  id: string;
  key: string;
  success: boolean;
  error?: string;
}

export async function uploadWithConcurrency(
  tasks: UploadTask[],
  jobId: string,
  maxConcurrency: number,
  onProgress: (id: string, progress: number) => void,
  onComplete: (id: string, key: string) => void,
  onError: (id: string, error: string) => void,
  signal: AbortSignal
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  const queue = [...tasks];
  const executing = new Set<Promise<void>>();

  const runOne = async (task: UploadTask): Promise<void> => {
    if (signal.aborted) return;
    try {
      onProgress(task.id, 10);
      const fileType = task.file.type || 'application/pdf';
      const urlResponse = await apiClient.post<UploadUrlResponse>(
        `/jobs/${jobId}/candidates/upload-url`,
        { fileName: task.file.name, fileType }
      );

      if (signal.aborted) return;
      onProgress(task.id, 40);

      await fetch(urlResponse.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileType },
        body: task.file,
        signal,
      });

      onProgress(task.id, 100);
      onComplete(task.id, urlResponse.key);
      results.push({ id: task.id, key: urlResponse.key, success: true });
    } catch (err: any) {
      if (signal.aborted) return;
      const msg = err.message || 'Falha no envio do arquivo';
      onError(task.id, msg);
      results.push({ id: task.id, key: '', success: false, error: msg });
    }
  };

  while (queue.length > 0 || executing.size > 0) {
    while (queue.length > 0 && executing.size < maxConcurrency) {
      if (signal.aborted) return results;
      const task = queue.shift()!;
      const p = runOne(task).then(() => { executing.delete(p); });
      executing.add(p);
    }
    if (executing.size > 0) {
      await Promise.race(executing);
    }
  }

  return results;
}
